import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  Checkbox, CircularProgress, Chip, FormControlLabel, Radio, RadioGroup,
  Backdrop, styled
} from '@mui/material';
import { Close, Add, Delete, Payment, Visibility, CheckCircle, Warning, Print } from '@mui/icons-material';
import { fetchData } from '../../../apiService';
import { useMobileSpacing } from '../../../utils/mobileSpacing';

// Theme Configuration for Payment/Receipt styling
const themeConfig = {
  payment: {
    primary: '#dc6363',
    secondary: '#ef4444',
    gradient: 'linear-gradient(135deg, #dc6363, #ef4444)',
    light: 'rgba(220, 38, 38, 0.1)',
    background: 'rgba(220, 38, 38, 0.05)',
    headerBg: 'linear-gradient(135deg, #dc6363 0%, #b91c1c 100%)',
    dialogBg: 'linear-gradient(135deg, rgba(220, 38, 38, 0.05), rgba(239, 68, 68, 0.02))',
    // Lighter versions
    lighter: '#f8a5a5',        // 40% lighter than primary
    lightest: '#fce4e4',       // 70% lighter than primary
    softBackground: 'rgba(220, 99, 99, 0.03)',
    paleAccent: '#fdf2f2'
  },
  receipt: {
    primary: '#429178',
    secondary: '#10b981',
    gradient: 'linear-gradient(135deg, #429178, #10b981)',
    light: 'rgba(5, 150, 105, 0.1)',
    background: 'rgba(5, 150, 105, 0.05)',
    headerBg: 'linear-gradient(135deg, #429178 0%, #047857 100%)',
    dialogBg: 'linear-gradient(135deg, rgba(5, 150, 105, 0.05), rgba(16, 185, 129, 0.02))',
    // Lighter versions
    lighter: '#7bc4a8',        // 40% lighter than primary
    lightest: '#e8f5f0',       // 70% lighter than primary
    softBackground: 'rgba(66, 145, 120, 0.03)',
    paleAccent: '#f0fdf4'
  }
};

// Professional Dialog with modern business styling
const StyledDialog = styled(Dialog)(({ theme, transactiontype }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '8px',
    background: '#ffffff',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
    border: 'none',
    overflow: 'hidden',
    position: 'relative',
    // Mobile-specific optimizations
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      margin: 0,
      maxHeight: '100vh',
      height: '100vh'
    }
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    [theme.breakpoints.down('sm')]: {
      backdropFilter: 'blur(2px)'
    }
  }
}));

// Professional TextField with compact styling
const StyledTextField = styled(TextField)(({ theme, transactiontype }) => {
  const config = transactiontype === 'payment' ? themeConfig.payment : themeConfig.receipt;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      backgroundColor: '#ffffff',
      height: '32px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d1d5db',
        borderWidth: '1px'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9ca3af',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: config.primary,
        borderWidth: '2px',
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#dc6363',
        borderWidth: '2px',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      fontSize: '13px',
      color: '#6b7280',
      '&.Mui-focused': {
        color: config.primary,
        fontWeight: 600,
      },
      '&.Mui-error': {
        color: '#dc6363',
      }
    },
    '& .MuiInputBase-input': {
      padding: '6px 10px',
      fontSize: '13px',
      fontWeight: 400,
      color: '#111827'
    },
    '& .MuiFormHelperText-root': {
      marginTop: '3px',
      fontSize: '11px',
      fontWeight: 400,
      '&.Mui-error': {
        color: '#dc6363',
        fontWeight: 500,
      }
    }
  };
});

// Professional FormControl with compact styling
const StyledFormControl = styled(FormControl)(({ theme, transactiontype }) => {
  const config = transactiontype === 'payment' ? themeConfig.payment : themeConfig.receipt;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      backgroundColor: '#ffffff',
      height: '32px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d1d5db',
        borderWidth: '1px'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9ca3af',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: config.primary,
        borderWidth: '2px',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      fontSize: '13px',
      color: '#6b7280',
      '&.Mui-focused': {
        color: config.primary,
        fontWeight: 600,
      }
    },
    '& .MuiSelect-select': {
      padding: '6px 10px',
      fontSize: '13px',
      fontWeight: 400,
      color: '#111827'
    }
  };
});

// Professional Button with compact styling
const StyledButton = styled(Button)(({ theme, variant, transactiontype }) => {
  const config = transactiontype === 'payment' ? themeConfig.payment : themeConfig.receipt;

  if (variant === 'contained') {
    return {
      background: config.primary,
      color: 'white',
      fontWeight: 600,
      borderRadius: '4px',
      textTransform: 'none',
      boxShadow: 'none',
      border: 'none',
      minHeight: '32px',
      minWidth: '80px',
      fontSize: '13px',
      fontFamily: 'inherit',
      '&:hover': {
        background: config.secondary,
        boxShadow: 'none',
      },
      '&:disabled': {
        background: '#e5e7eb',
        color: '#9ca3af',
        boxShadow: 'none',
      }
    };
  }

  return {
    borderColor: '#d1d5db',
    color: '#374151',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'none',
    borderWidth: '1px',
    minHeight: '32px',
    minWidth: '80px',
    fontSize: '13px',
    fontFamily: 'inherit',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#9ca3af',
      backgroundColor: '#f9fafb',
      boxShadow: 'none',
    }
  };
});

// Professional Loading Overlay Component
const LoadingOverlay = ({ loading, transactionType }) => (
  <Backdrop
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '12px'
    }}
    open={loading}
  >
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3,
      textAlign: 'center'
    }}>
      {/* Simple Loading Spinner */}
      <CircularProgress
        size={48}
        sx={{
          color: transactionType === 'payment' ? '#dc6363' : '#429178'
        }}
      />

      {/* Loading Text */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            color: transactionType === 'payment' ? '#dc6363' : '#429178',
            fontWeight: 600,
            mb: 1,
            fontSize: '18px'
          }}
        >
          Loading {transactionType === 'payment' ? 'Payment' : 'Receipt'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '14px'
          }}
        >
          Please wait while we prepare your form...
        </Typography>
      </Box>
    </Box>
  </Backdrop>
);

const PaymentReceiptsModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  paymentReceiptDetails,
  setPaymentReceiptDetails,
  onSave,
  masters,
  banks,
  purchases,
  sales,
  paymentMethods,
  gstRates,
  loading,
  companyId,
  token,
  fetchPurchases,
  fetchSales,
  fetchBillsForMaster,
  fetchMaxBillNo,
  branchId,
  fromDate,
  toDate,
  onPrint
}) => {
  const theme = useTheme();

  // Use the new mobile spacing utilities hook
  const mobileSpacing = useMobileSpacing();

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Loading state for enhanced UX
  const [modalLoading, setModalLoading] = useState(false);

  // Master Type selection state
  const [headType, setHeadType] = useState('AccountHead'); // Default to AccountHead
  const [dynamicHeads, setDynamicHeads] = useState([]);
  const [loadingHeads, setLoadingHeads] = useState(false);

  // Cache all data for performance - fetch once, use many times
  const [allVendors, setAllVendors] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allBanks, setAllBanks] = useState([]);
  const [allAccountHeads, setAllAccountHeads] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const isViewMode = modalAction === 'view';
  const transactionType = formData.IsType ? 'payment' : 'receipt';
  const currentTheme = transactionType === 'payment' ? themeConfig.payment : themeConfig.receipt;

  // Helper function to convert MasterType number to string
  const getMasterTypeString = useCallback((masterType) => {
    const numType = typeof masterType === 'string' ? parseInt(masterType, 10) : masterType;

    switch (numType) {
      case 1:
        return 'Vendor';
      case 2:
        return 'Customer';
      case 3:
        return 'Bank';
      case 4:
        return 'AccountHead';
      default:
        return 'AccountHead'; // Default
    }
  }, []);

  // Helper function to convert MasterType string to number
  const getMasterTypeNumber = useCallback((masterTypeString) => {
    switch (masterTypeString) {
      case 'Vendor':
        return 1;
      case 'Customer':
        return 2;
      case 'Bank':
        return 3;
      case 'AccountHead':
        return 4;
      default:
        return 4; // Default to AccountHead
    }
  }, []);

  // Helper function to detect type from ID fields (fallback if MasterType is not available)
  const detectTypeFromIds = useCallback((data) => {
    if (data.VendorId) return 'Vendor';
    if (data.CustomerId) return 'Customer';
    if (data.BankId) return 'Bank';
    if (data.AccountHeadId) return 'AccountHead';
    return 'AccountHead'; // Default
  }, []);

  // Dynamic title and colors based on type
  const getTypeBasedContent = () => {
    const isPayment = formData.IsType;

    if (modalAction === 'create') {
      return {
        title: isPayment ? 'Create Payment' : 'Create Receipt',
        headerColor: isPayment ? '#dc6363' : '#429178', // Red for Payment, Green for Receipt
        iconColor: isPayment ? '#dc6363' : '#429178',
        infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
        billNoLabel: isPayment ? 'Voucher No *' : 'Receipt No *',
        billDateLabel: isPayment ? 'Voucher Date *' : 'Receipt Date *'
      };
    } else if (modalAction === 'edit') {
      return {
        title: isPayment ? 'Edit Payment' : 'Edit Receipt',
        headerColor: isPayment ? '#dc6363' : '#429178',
        iconColor: isPayment ? '#dc6363' : '#429178',
        infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
        billNoLabel: isPayment ? 'Voucher No *' : 'Receipt No *',
        billDateLabel: isPayment ? 'Voucher Date *' : 'Receipt Date *'
      };
    } else {
      return {
        title: isPayment ? 'View Payment' : 'View Receipt',
        headerColor: isPayment ? '#dc6363' : '#429178',
        iconColor: isPayment ? '#dc6363' : '#429178',
        infoCaption: isPayment ? 'Payment Information' : 'Receipt Information',
        billNoLabel: isPayment ? 'Voucher No' : 'Receipt No',
        billDateLabel: isPayment ? 'Voucher Date' : 'Receipt Date'
      };
    }
  };

  const typeContent = getTypeBasedContent();

  // Fetch all data once for performance optimization
  const fetchAllData = useCallback(async () => {
    if (dataLoaded) {
      return; // Don't fetch if already loaded
    }

    setLoadingHeads(true);
    try {
      // Fetch all data in parallel
      const [vendorsRes, customersRes, banksRes, accountHeadsRes] = await Promise.all([
        fetchData('VendorApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('CustomerApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('AccountHeadApi', `/${companyId}`, token).catch(() => ({ response_data: [] }))
      ]);

      const vendors = vendorsRes.response_data || vendorsRes || [];
      const customers = customersRes.response_data || customersRes || [];
      const banksData = banksRes.response_data || banksRes || [];
      const accountHeads = accountHeadsRes.response_data || accountHeadsRes || [];

      setAllVendors(vendors);
      setAllCustomers(customers);
      setAllBanks(banksData);
      setAllAccountHeads(accountHeads);
      setDataLoaded(true);

      // Don't set dynamicHeads here - let the headType useEffect handle it based on current headType
      console.log('=== DATA FETCHED ===');
      console.log('Vendors:', vendors.length);
      console.log('Customers:', customers.length);
      console.log('Banks:', banksData.length);
      console.log('Account Heads:', accountHeads.length);
      console.log('===================');
    } catch (error) {
      console.error('Error fetching all data:', error);
    } finally {
      setLoadingHeads(false);
    }
  }, [companyId, token, dataLoaded]);

  // Get data based on selected type (from cache)
  const getDataByType = useCallback((type) => {
    let data = [];
    switch (type) {
      case 'Vendor':
        data = allVendors;
        break;
      case 'Customer':
        data = allCustomers;
        break;
      case 'Bank':
        data = allBanks;
        break;
      case 'AccountHead':
      default:
        data = allAccountHeads;
        break;
    }
    return data;
  }, [allVendors, allCustomers, allBanks, allAccountHeads]);

  // Set head type based on formData when editing/viewing
  useEffect(() => {
    // Only run for edit/view mode when modal is open
    if (!open || (modalAction !== 'edit' && modalAction !== 'view')) {
      return;
    }

    // Set main head type if MasterId exists
    if (formData?.MasterId) {
      // Try to get type from MasterType field, fallback to detecting from ID fields
      let detectedType;
      if (formData.MasterType !== undefined && formData.MasterType !== null) {
        detectedType = getMasterTypeString(formData.MasterType);
      } else {
        detectedType = detectTypeFromIds(formData);
      }

      setHeadType(detectedType);

      // If data is not loaded yet, fetch it first
      if (!dataLoaded) {
        fetchAllData();
        return;
      }

      // Update main head dropdown data
      setDynamicHeads(getDataByType(detectedType));
    }
  }, [open, modalAction, formData, dataLoaded, allVendors, allCustomers, allBanks, allAccountHeads, getMasterTypeString, detectTypeFromIds, fetchAllData, getDataByType]);

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);

      // ONLY set default head type for CREATE mode
      if (modalAction === 'create') {
        setHeadType('AccountHead');

        // Fetch data if needed
        const loadData = async () => {
          if (!dataLoaded) {
            await fetchAllData();
          }
          // Don't set dynamicHeads here - let the headType useEffect handle it
        };
        loadData();
      } else {
        // For edit/view, just ensure data is loaded
        if (!dataLoaded) {
          fetchAllData();
        }
      }

      // Reduce loading time for better UX
      setModalLoading(true);
      const timer = setTimeout(() => {
        setModalLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setModalLoading(false);
      setDynamicHeads([]);
      setHeadType('AccountHead');
    }
  }, [open, modalAction, dataLoaded, fetchAllData]);

  // Fetch heads when head type changes (uses cached data)
  // Force re-render when type changes to update colors
  useEffect(() => {
    // This effect ensures the component re-renders when IsType changes
    // to update the dynamic colors and captions
  }, [formData.IsType]);

  // Fetch heads when head type changes (uses cached data)
  useEffect(() => {
    if (open && headType) {
      console.log('=== HEAD TYPE CHANGED ===');
      console.log('Current headType:', headType);
      console.log('Data loaded:', dataLoaded);
      console.log('All Vendors:', allVendors.length);
      console.log('All Customers:', allCustomers.length);
      console.log('All Banks:', allBanks.length);
      console.log('All Account Heads:', allAccountHeads.length);

      // If data is not loaded yet, fetch it first
      if (!dataLoaded) {
        console.log('Data not loaded, fetching...');
        fetchAllData().then(() => {
          // Data loaded, now set dynamicHeads
          console.log('Data fetched successfully');
        });
        return; // Exit early, will re-run when dataLoaded becomes true
      }

      // Get data directly based on type
      const newData = getDataByType(headType);
      console.log('Setting dynamicHeads for', headType, ':', newData.length, 'items');
      setDynamicHeads(newData);

      // Clear selected MasterId when changing head type (only if in create mode)
      if (modalAction === 'create') {
        setFormData(prev => ({ ...prev, MasterId: '' }));
      }
      console.log('=== END HEAD TYPE CHANGE ===');
    }
  }, [headType, open, dataLoaded, modalAction, getDataByType, fetchAllData, setFormData, allVendors, allCustomers, allBanks, allAccountHeads]);

  // Smart distribution of amount across bills
  const distributeAmountAcrossBills = useCallback((totalAmount) => {
    if (!paymentReceiptDetails || paymentReceiptDetails.length === 0 || paymentReceiptDetails[0]?.noData) return;

    const updatedDetails = [...paymentReceiptDetails];
    let remainingAmount = parseFloat(totalAmount) || 0;

    // Reset all selections and amounts first
    for (let i = 0; i < updatedDetails.length; i++) {
      updatedDetails[i].IsSelected = false;
      updatedDetails[i].PaymentAmount = 0;
    }

    // If amount is 0 or negative, keep everything unselected
    if (remainingAmount <= 0) {
      setPaymentReceiptDetails(updatedDetails);
      return;
    }

    // Calculate total pending amount
    const totalPendingAmount = updatedDetails.reduce((sum, detail) => sum + (detail.PendingAmount || 0), 0);

    // If entered amount equals total pending, select all bills with full amounts
    if (remainingAmount >= totalPendingAmount) {
      for (let i = 0; i < updatedDetails.length; i++) {
        updatedDetails[i].IsSelected = true;
        updatedDetails[i].PaymentAmount = updatedDetails[i].PendingAmount || 0;
      }
    } else {
      // Distribute amount intelligently - full amounts first, then partial for last bill
      for (let i = 0; i < updatedDetails.length && remainingAmount > 0; i++) {
        const detail = updatedDetails[i];
        const pendingAmount = detail.PendingAmount || 0;

        if (remainingAmount >= pendingAmount) {
          // Full payment for this bill
          detail.IsSelected = true;
          detail.PaymentAmount = pendingAmount;
          remainingAmount -= pendingAmount;
        } else if (remainingAmount > 0) {
          // Partial payment for this bill (remaining amount)
          detail.IsSelected = true;
          detail.PaymentAmount = remainingAmount;
          remainingAmount = 0;
        }
      }
    }

    setPaymentReceiptDetails(updatedDetails);
  }, [paymentReceiptDetails]);

  // Calculate totals without GST
  const calculateTotals = useCallback(() => {
    const amount = parseFloat(formData.Amount) || 0;
    const roundOff = Math.round(amount) - amount;
    const totalAmount = Math.round(amount);

    setFormData(prev => ({
      ...prev,
      RoundOff: Math.round(roundOff * 100) / 100,
      TotalAmount: totalAmount
    }));
  }, [formData.Amount]);

  // Handle purchases and sales data updates after master selection
  useEffect(() => {
    // Only run this for create mode - edit/view mode gets data directly from API
    if (modalAction === 'create' && formData.MasterId && masters.length > 0) {
      const selectedMaster = masters.find(m => m.Id === parseInt(formData.MasterId));

      if (selectedMaster) {
        console.log('useEffect triggered - Master:', selectedMaster.Name, 'Transaction Type:', formData.IsType ? 'Payment' : 'Receipt');
        console.log('Purchases length:', purchases.length, 'Sales length:', sales.length);

        // Handle based on transaction type (Payment vs Receipt) - NOT master type
        if (formData.IsType) {
          // Payment - show purchase bills (regardless of master type)
          if (purchases.length > 0) {
            console.log('Loading purchase bills for payment');
            const purchaseDetails = purchases.map(p => ({
              Id: p.PurchaseId,
              Description: p.PurchaseDescription,
              Amount: p.PurchaseAmount,
              PaidAmount: p.PaidAmount,
              BalanceAmount: p.BalanceAmount,
              PendingAmount: p.PendingAmount,
              BillDate: p.BillDate,
              Type: 'Purchase',
              PaymentAmount: 0,
              IsSelected: false
            }));

            setPaymentReceiptDetails(purchaseDetails);

            // Set total pending amount in the main Amount field only for create mode
            const totalPending = purchaseDetails.reduce((sum, p) => sum + (p.PendingAmount || 0), 0);
            setFormData(prev => ({ ...prev, Amount: totalPending, TotalAmount: totalPending }));
          }
        } else {
          // Receipt - show sale bills (regardless of master type)
          if (sales.length > 0) {
            console.log('Loading sale bills for receipt');
            const saleDetails = sales.map(s => ({
              Id: s.SaleId,
              Description: s.SaleDescription,
              Amount: s.SaleAmount,
              PaidAmount: s.PaidAmount,
              BalanceAmount: s.BalanceAmount,
              PendingAmount: s.PendingAmount,
              BillDate: s.BillDate,
              Type: 'Sale',
              PaymentAmount: 0,
              IsSelected: false
            }));

            setPaymentReceiptDetails(saleDetails);

            // Set total pending amount in the main Amount field only for create mode
            const totalPending = saleDetails.reduce((sum, s) => sum + (s.PendingAmount || 0), 0);
            setFormData(prev => ({ ...prev, Amount: totalPending, TotalAmount: totalPending }));
          }
        }

        // If no data is available after a timeout, show no data message
        if (purchases.length === 0 && sales.length === 0) {
          console.log('No bills found for this master and transaction type');
          const timer = setTimeout(() => {
            if (paymentReceiptDetails.length === 0) {
              setPaymentReceiptDetails([{ noData: true }]);
            }
          }, 2000);

          return () => clearTimeout(timer);
        }
      }
    }
  }, [formData.MasterId, formData.IsType, purchases, sales, masters, modalAction]);

  // Auto-distribute amount when bills are loaded and amount is set
  useEffect(() => {
    if (paymentReceiptDetails.length > 0 && formData.Amount && !paymentReceiptDetails[0]?.noData) {
      const timeoutId = setTimeout(() => {
        distributeAmountAcrossBills(formData.Amount);
      }, 150); // Debounce distribution

      return () => clearTimeout(timeoutId);
    }
  }, [paymentReceiptDetails.length, formData.Amount, distributeAmountAcrossBills]);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    const typeContent = getTypeBasedContent();

    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') {
          return `${typeContent.billNoLabel.replace(' *', '')} is required`;
        }
        return '';
      case 'BillDate':
        if (!value) {
          return `${typeContent.billDateLabel.replace(' *', '')} is required`;
        }
        return '';
      case 'MasterId':
        if (!value) return 'Master is required';
        return '';
      case 'Amount':
        if (!value || parseFloat(value) <= 0) return 'Amount must be greater than 0';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    const masterError = validateField('MasterId', formData.MasterId);
    if (masterError) newErrors.MasterId = masterError;

    const amountError = validateField('Amount', formData.Amount);
    if (amountError) newErrors.Amount = amountError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ BillNumber: true, BillDate: true, MasterId: true, Amount: true });

    // Build validation error messages
    const validationMessages = [];
    const typeContent = getTypeBasedContent();

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push(`${typeContent.billNoLabel.replace(' *', '')} is required`);
    }
    if (!formData.BillDate) {
      validationMessages.push(`${typeContent.billDateLabel.replace(' *', '')} is required`);
    }
    if (!formData.MasterId) {
      validationMessages.push('Master is required');
    }
    if (!formData.Amount || parseFloat(formData.Amount) <= 0) {
      validationMessages.push('Amount must be greater than 0');
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    if (field === 'MasterId') {
      const selectedMaster = dynamicHeads.find(m => m.Id === parseInt(value));

      // Clear details when master changes
      setPaymentReceiptDetails([]);

      // Set the appropriate ID field and MasterType based on head type
      const updates = {
        MasterId: value,
        MasterType: getMasterTypeNumber(headType), // Set MasterType based on current headType
        MasterName: selectedMaster?.Name || '',
        IsSaleAndPurchase: !!selectedMaster,
        // Clear all ID fields first
        CustomerId: null,
        VendorId: null,
        AccountHeadId: null,
        BankId: null
      };

      // Set the correct ID field based on head type
      switch (headType) {
        case 'Vendor':
          updates.VendorId = parseInt(value);
          break;
        case 'Customer':
          updates.CustomerId = parseInt(value);
          break;
        case 'Bank':
          updates.BankId = parseInt(value);
          break;
        case 'AccountHead':
          updates.AccountHeadId = parseInt(value);
          break;
        default:
          break;
      }

      setFormData(prev => ({ ...prev, ...updates }));

      // Fetch bills based on master type (Vendor -> Purchases, Customer -> Sales)
      if (selectedMaster && fetchBillsForMaster) {
        try {
          fetchBillsForMaster(parseInt(value), formData.IsType, headType);
        } catch (error) {
          console.error('Error fetching bills for master:', error);
        }
      }
    } else if (field === 'PaymentType') {
      // Clear bank related fields when payment type changes to Cash
      if (parseInt(value) === 1) { // Cash
        setFormData(prev => ({
          ...prev,
          [field]: parseInt(value),
          ChequeNumber: '',
          ChequeDate: '',
          BankId: null,
          chequeDetails: '',
          ChequeStatusId: 1
        }));
      } else if (parseInt(value) === 2) { // Cheque
        setFormData(prev => ({
          ...prev,
          [field]: parseInt(value),
          ChequeStatusId: 2 // Auto-set to "Passed" when cheque is selected
        }));
      } else {
        setFormData(prev => ({ ...prev, [field]: parseInt(value) }));
      }
    } else if (field === 'IsType') {
      // Handle Payment/Receipt type change and fetch new bill number
      const isPayment = value === true || value === 'payment';
      setFormData(prev => ({ ...prev, [field]: isPayment }));

      // Clear existing details when type changes
      setPaymentReceiptDetails([]);

      // Refetch bills for the current master with the new transaction type
      if (formData.MasterId && fetchBillsForMaster) {
        fetchBillsForMaster(parseInt(formData.MasterId), isPayment, headType);
      }

      // Fetch new max bill number based on the selected type
      if (fetchMaxBillNo && modalAction === 'create') {
        fetchMaxBillNo(isPayment).then(maxBillData => {
          const billNumber = maxBillData.billPrefix && maxBillData.finYear ?
            `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
            maxBillData.billNumber.toString();

          setFormData(prev => ({ ...prev, BillNumber: billNumber }));
        }).catch(error => {
          console.error('Error fetching max bill number:', error);
        });
      }
    } else if (field === 'Amount') {
      setFormData(prev => ({ ...prev, [field]: value }));

      // If Amount field changed, distribute the amount across bills
      distributeAmountAcrossBills(value);
    } else if (field === 'BankId') {
      // Handle BankId with proper type conversion
      setFormData(prev => ({
        ...prev,
        [field]: value ? parseInt(value) : null
      }));
    } else if (field === 'ChequeStatusId') {
      // Handle ChequeStatusId with proper type conversion
      setFormData(prev => ({
        ...prev,
        [field]: parseInt(value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle detail selection and amount change
  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...paymentReceiptDetails];

    if (field === 'IsSelected') {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      if (!value) {
        updatedDetails[index].PaymentAmount = 0;
      } else {
        // When selected, default to the pending amount
        updatedDetails[index].PaymentAmount = updatedDetails[index].PendingAmount || 0;
      }
    } else if (field === 'PaymentAmount') {
      const numValue = parseFloat(value) || 0;
      const maxAmount = updatedDetails[index].PendingAmount || 0;

      if (numValue > maxAmount) {
        showError(`Amount cannot exceed pending amount of ${formatCurrency(maxAmount)}`);
        return;
      }

      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]: numValue,
        IsSelected: numValue > 0
      };
    }

    setPaymentReceiptDetails(updatedDetails);

    // Recalculate total amount
    const totalSelectedAmount = updatedDetails
      .filter(d => d.IsSelected && d.PaymentAmount > 0)
      .reduce((sum, d) => sum + (parseFloat(d.PaymentAmount) || 0), 0);

    setFormData(prev => ({
      ...prev,
      Amount: totalSelectedAmount,
      TotalAmount: totalSelectedAmount
    }));
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getSelectedMaster = () => {
    if (!formData.MasterId) return null;
    // Try to find in dynamicHeads first (filtered list), then fall back to masters
    return dynamicHeads.find(m => m.Id === parseInt(formData.MasterId)) ||
      masters.find(m => m.Id === parseInt(formData.MasterId));
  };

  const selectedMaster = getSelectedMaster();

  return (
    <>
      <StyledDialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) {
              setCancelConfirmOpen(true);
              return;
            }
          }
          onClose();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={mobileSpacing.isMobile}
        disableEscapeKeyDown
        transactiontype={transactionType}
        PaperProps={{
          sx: {
            borderRadius: mobileSpacing.isMobile ? 0 : '8px',
            m: mobileSpacing.isMobile ? 0 : 1,
            maxWidth: '1000px',
            position: 'relative',
            overflow: 'hidden'
          }
        }}
      >

        {/* Loading Overlay */}
        <LoadingOverlay loading={modalLoading} transactionType={transactionType} />

        <DialogTitle sx={{
          py: 1.5,
          px: 2,
          background: `${currentTheme.primary}`,
          color: 'white',
          borderBottom: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Payment sx={{ color: 'white', fontSize: 16 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'inherit'
                }}
              >
                {typeContent.title}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCancelClick}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{
          p: 1.5,
          background: '#f9fafb',
          minHeight: '300px'
        }}>
          <Box sx={{
            background: '#ffffff',
            borderRadius: '4px',
            p: 1.5,
            border: '1px solid #e5e7eb',
            boxShadow: 'none'
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: currentTheme.primary,
                mb: 1.5,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontFamily: 'inherit'
              }}
            >
              {typeContent.infoCaption}
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={6} sm={1.5}>
                <StyledTextField
                  fullWidth
                  size="small"
                  label={typeContent.billNoLabel}
                  value={formData.BillNumber || ''}
                  onChange={(e) => handleChange('BillNumber', e.target.value)}
                  onBlur={() => handleBlur('BillNumber')}
                  disabled={isViewMode}
                  error={touched.BillNumber && !!errors.BillNumber}
                  helperText={touched.BillNumber && errors.BillNumber}
                  transactiontype={transactionType}
                />
              </Grid>

              <Grid item xs={6} sm={1.8}>
                <StyledTextField
                  fullWidth
                  size="small"
                  type="date"
                  label={typeContent.billDateLabel}
                  value={formData.BillDate || ''}
                  onChange={(e) => handleChange('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  disabled={isViewMode}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }}
                  transactiontype={transactionType}
                />
              </Grid>

              <Grid item xs={6} sm={1.5}>
                <StyledFormControl
                  fullWidth
                  size="small"
                  transactiontype={transactionType}
                >
                  <InputLabel>Type *</InputLabel>
                  <Select
                    value={formData.IsType ? 'payment' : 'receipt'}
                    onChange={(e) => handleChange('IsType', e.target.value === 'payment')}
                    label="Type *"
                    disabled={isViewMode}
                    sx={{
                      '& .MuiSelect-select': {
                        color: currentTheme.primary,
                        fontWeight: 600
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #e5e7eb'
                        }
                      }
                    }}
                  >
                    <MenuItem
                      value="payment"
                      sx={{
                        fontSize: '14px',
                        py: 1,
                        px: 2,
                        color: themeConfig.payment.primary,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: themeConfig.payment.background
                        },
                        '&.Mui-selected': {
                          backgroundColor: themeConfig.payment.light,
                          '&:hover': {
                            backgroundColor: themeConfig.payment.light
                          }
                        }
                      }}
                    >
                      Payment
                    </MenuItem>
                    <MenuItem
                      value="receipt"
                      sx={{
                        fontSize: '14px',
                        py: 1,
                        px: 2,
                        color: themeConfig.receipt.primary,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: themeConfig.receipt.background
                        },
                        '&.Mui-selected': {
                          backgroundColor: themeConfig.receipt.light,
                          '&:hover': {
                            backgroundColor: themeConfig.receipt.light
                          }
                        }
                      }}
                    >
                      Receipt
                    </MenuItem>
                  </Select>
                </StyledFormControl>
              </Grid>

              <Grid item xs={6} sm={1.8}>
                <StyledFormControl
                  fullWidth
                  size="small"
                  transactiontype={transactionType}
                >
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    key={`payment-method-${formData.PaymentType || 1}`}
                    value={formData.PaymentType || 1}
                    onChange={(e) => handleChange('PaymentType', e.target.value)}
                    label="Payment Method"
                    disabled={isViewMode}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #e5e7eb',
                          '& .MuiMenuItem-root': {
                            fontSize: '14px',
                            py: 1,
                            px: 2,
                            fontWeight: 400,
                            '&:hover': {
                              backgroundColor: '#f3f4f6'
                            }
                          }
                        }
                      }
                    }}
                  >
                    {paymentMethods && paymentMethods.length > 0 ? (
                      paymentMethods
                        .filter(pm => pm.IsActive !== false)
                        .map(pm => (
                          <MenuItem key={pm.Id} value={pm.Id}>
                            {pm.Name}
                          </MenuItem>
                        ))
                    ) : (
                      // Fallback options if API data is not available
                      <>
                        <MenuItem value={1}>Cash</MenuItem>
                        <MenuItem value={2}>Cheque</MenuItem>
                        <MenuItem value={3}>Online Transfer</MenuItem>
                      </>
                    )}
                  </Select>
                </StyledFormControl>
              </Grid>

              {/* Master Type Selection - Radio Buttons and Amount */}
              {!isViewMode ? (
                <>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      height: '32px'
                    }}>
                      <RadioGroup
                        row
                        value={headType}
                        onChange={(e) => setHeadType(e.target.value)}
                        sx={{ gap: 0.25, flexWrap: 'nowrap', margin: 0 }}
                      >
                        <FormControlLabel
                          value="Vendor"
                          control={<Radio size="small" sx={{
                            color: currentTheme.primary,
                            '&.Mui-checked': { color: currentTheme.primary },
                            padding: '2px'
                          }} />}
                          label={<Typography sx={{ fontSize: '10px', fontWeight: 500 }}>Vendor</Typography>}
                          sx={{ margin: 0, marginRight: 0.25 }}
                        />
                        <FormControlLabel
                          value="Customer"
                          control={<Radio size="small" sx={{
                            color: currentTheme.primary,
                            '&.Mui-checked': { color: currentTheme.primary },
                            padding: '2px'
                          }} />}
                          label={<Typography sx={{ fontSize: '10px', fontWeight: 500 }}>Customer</Typography>}
                          sx={{ margin: 0, marginRight: 0.25 }}
                        />
                        <FormControlLabel
                          value="Bank"
                          control={<Radio size="small" sx={{
                            color: currentTheme.primary,
                            '&.Mui-checked': { color: currentTheme.primary },
                            padding: '2px'
                          }} />}
                          label={<Typography sx={{ fontSize: '10px', fontWeight: 500 }}>Bank</Typography>}
                          sx={{ margin: 0, marginRight: 0.25 }}
                        />
                        <FormControlLabel
                          value="AccountHead"
                          control={<Radio size="small" sx={{
                            color: currentTheme.primary,
                            '&.Mui-checked': { color: currentTheme.primary },
                            padding: '2px'
                          }} />}
                          label={<Typography sx={{ fontSize: '10px', fontWeight: 500 }}>Account Head</Typography>}
                          sx={{ margin: 0 }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={1.5}>
                    <StyledTextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Amount *"
                      value={formData.Amount || ''}
                      onChange={(e) => handleChange('Amount', e.target.value)}
                      onBlur={() => handleBlur('Amount')}
                      disabled={isViewMode}
                      error={touched.Amount && !!errors.Amount}
                      helperText={touched.Amount && errors.Amount}
                      inputProps={{ min: 0, step: 0.01 }}
                      transactiontype={transactionType}
                      sx={{
                        '& .MuiInputBase-input': {
                          py: '6px',
                          px: '10px',
                          fontSize: '13px',
                          fontWeight: 500,
                          textAlign: 'right'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '13px',
                          fontWeight: 500
                        },
                        '& .MuiOutlinedInput-root': {
                          height: '32px'
                        }
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={1.5}>
                  <StyledTextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Amount *"
                    value={formData.Amount || ''}
                    onChange={(e) => handleChange('Amount', e.target.value)}
                    onBlur={() => handleBlur('Amount')}
                    disabled={isViewMode}
                    error={touched.Amount && !!errors.Amount}
                    helperText={touched.Amount && errors.Amount}
                    inputProps={{ min: 0, step: 0.01 }}
                    transactiontype={transactionType}
                    sx={{
                      '& .MuiInputBase-input': {
                        py: '6px',
                        px: '10px',
                        fontSize: '13px',
                        fontWeight: 500,
                        textAlign: 'right'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '13px',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-root': {
                        height: '32px'
                      }
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={isViewMode ? 5.1 : 5.1}>
                {isViewMode ? (
                  <StyledTextField
                    fullWidth
                    size="small"
                    label="Master"
                    value={selectedMaster?.Name || '-'}
                    disabled
                    transactiontype={transactionType}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        color: currentTheme.primary,
                        fontWeight: 600,
                        WebkitTextFillColor: currentTheme.primary
                      }
                    }}
                  />
                ) : (
                  <StyledFormControl
                    fullWidth
                    size="small"
                    error={touched.MasterId && !!errors.MasterId}
                    transactiontype={transactionType}
                  >
                    <InputLabel>Master *</InputLabel>
                    <Select
                      value={formData.MasterId || ''}
                      onChange={(e) => handleChange('MasterId', e.target.value)}
                      onBlur={() => handleBlur('MasterId')}
                      label="Master *"
                      disabled={loadingHeads}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 200,
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '1px solid #e5e7eb',
                            '& .MuiMenuItem-root': {
                              fontSize: '14px',
                              py: 1,
                              px: 2,
                              fontWeight: 400,
                              '&:hover': {
                                backgroundColor: '#f3f4f6'
                              },
                              '&.Mui-selected': {
                                backgroundColor: currentTheme.light,
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: currentTheme.light
                                }
                              }
                            }
                          }
                        }
                      }}
                    >
                      {dynamicHeads.length === 0 ? (
                        <MenuItem disabled>
                          <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
                            {loadingHeads ? 'Loading...' : `No ${headType} available`}
                          </Typography>
                        </MenuItem>
                      ) : (
                        dynamicHeads.map(m => (
                          <MenuItem key={m.Id} value={m.Id}>
                            {m.Name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.MasterId && errors.MasterId && <FormHelperText>{errors.MasterId}</FormHelperText>}
                  </StyledFormControl>
                )}
              </Grid>

              <Grid item xs={12} sm={5.4}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '11px', fontWeight: 500 }}>Party</InputLabel>
                  <Select
                    value={formData.PartyId || ''}
                    onChange={(e) => handleChange('PartyId', e.target.value)}
                    disabled={isViewMode}
                    sx={{
                      '& .MuiSelect-select': {
                        py: '6px',
                        px: '10px',
                        fontSize: '11px',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-root': {
                        height: '32px'
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '11px' }}>
                      <em>Select Party</em>
                    </MenuItem>
                    {(formData.IsType ? allVendors : allCustomers)
                      .map((party) => (
                        <MenuItem
                          key={party.Id}
                          value={party.Id}
                          sx={{
                            fontSize: '11px',
                            py: 0.5,
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#e3f2fd'
                              }
                            }
                          }}
                        >
                          {party.Name}
                        </MenuItem>
                      ))}
                  </Select>
                </StyledFormControl>
              </Grid>

              <Grid item xs={12} sm={12}>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Remarks"
                  value={formData.Remarks || ''}
                  onChange={(e) => handleChange('Remarks', e.target.value)}
                  disabled={isViewMode}
                  transactiontype={transactionType}
                  InputLabelProps={{
                    sx: {
                      fontSize: '13px',
                      fontWeight: 500
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '13px',
                      fontWeight: 500,
                      py: '6px',
                      px: '10px'
                    },
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: '#ccc'
                      }
                    }
                  }}
                />
              </Grid>

            </Grid>

            {/* Bank Details - Show only when Payment Type is not Cash */}
            {formData.PaymentType !== 1 && (
              <Box sx={{
                background: '#ffffff',
                borderRadius: '4px',
                p: 1.5,
                mb: 1.5,
                border: '1px solid #e5e7eb',
                boxShadow: 'none'
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: currentTheme.primary,
                    mb: 1,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontFamily: 'inherit'
                  }}
                >
                  Bank Details
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}>
                    <StyledTextField
                      fullWidth
                      size="small"
                      label="Reference Number"
                      value={formData.ChequeNumber || ''}
                      onChange={(e) => handleChange('ChequeNumber', e.target.value)}
                      disabled={isViewMode}
                      transactiontype={transactionType}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <StyledTextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Transaction Date"
                      value={formData.ChequeDate || ''}
                      onChange={(e) => handleChange('ChequeDate', e.target.value)}
                      disabled={isViewMode}
                      InputLabelProps={{ shrink: true }}
                      transactiontype={transactionType}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <StyledFormControl
                      fullWidth
                      size="small"
                      transactiontype={transactionType}
                    >
                      <InputLabel>Bank</InputLabel>
                      <Select
                        key={`bank-select-${formData.PaymentType}`}
                        value={formData.BankId || ''}
                        onChange={(e) => handleChange('BankId', e.target.value)}
                        label="Bank"
                        disabled={isViewMode}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: '4px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              border: '1px solid #e5e7eb',
                              '& .MuiMenuItem-root': {
                                fontSize: '13px',
                                py: 0.5,
                                px: 1.5,
                                fontWeight: 400,
                                '&:hover': {
                                  backgroundColor: '#f3f4f6'
                                }
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Bank</em>
                        </MenuItem>
                        {banks.map(b => (
                          <MenuItem key={b.Id} value={b.Id}>
                            {b.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <StyledTextField
                      fullWidth
                      size="small"
                      label="Transaction Details"
                      value={formData.chequeDetails || ''}
                      onChange={(e) => handleChange('chequeDetails', e.target.value)}
                      disabled={isViewMode}
                      transactiontype={transactionType}
                    />
                  </Grid>

                  {/* Cheque Status - Show only when Payment Type is Cheque */}
                  {formData.PaymentType === 2 && (
                    <Grid item xs={12} sm={3}>
                      <StyledFormControl
                        fullWidth
                        size="small"
                        transactiontype={transactionType}
                      >
                        <InputLabel>Cheque Status</InputLabel>
                        <Select
                          value={formData.ChequeStatusId || 1}
                          onChange={(e) => handleChange('ChequeStatusId', e.target.value)}
                          label="Cheque Status"
                          disabled={isViewMode}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: '4px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                border: '1px solid #e5e7eb',
                                '& .MuiMenuItem-root': {
                                  fontSize: '13px',
                                  py: 0.5,
                                  px: 1.5,
                                  fontWeight: 400,
                                  '&:hover': {
                                    backgroundColor: '#f3f4f6'
                                  }
                                }
                              }
                            }
                          }}
                        >
                          <MenuItem value={1}>Pending</MenuItem>
                          <MenuItem value={2}>Passed</MenuItem>
                          <MenuItem value={3}>Bounced</MenuItem>
                          <MenuItem value={4}>Cancelled</MenuItem>
                        </Select>
                      </StyledFormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Auto-set Cheque Status to Passed when Payment Type is Cheque */}
            {formData.PaymentType === 2 && modalAction === 'create' && formData.ChequeStatusId === 1 && (
              <Box sx={{
                background: '#e8f5e8',
                borderRadius: '4px',
                p: 1,
                mb: 1.5,
                border: '1px solid #4caf50',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    color: '#2e7d32',
                    fontWeight: 500
                  }}
                >
                  Tip: You can set the cheque status to "Passed" once the cheque is cleared by the bank.
                </Typography>
              </Box>
            )}

            {/* Purchase/Sale Details Section */}
            {formData.MasterId && !(paymentReceiptDetails.length > 0 && paymentReceiptDetails[0]?.isDirectPayment) && (
              <>
                <Box sx={{
                  border: `1px solid #e0e0e0`,
                  borderRadius: '8px',
                  p: 1.5,
                  backgroundColor: '#fafafa'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#333',
                        fontSize: '13px'
                      }}
                    >
                      {formData.IsType ? 'Purchase Bills' : 'Sale Bills'}
                    </Typography>
                    {paymentReceiptDetails.length > 0 && (
                      <Chip
                        label={`${paymentReceiptDetails.length} Bills`}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 500,
                          fontSize: '11px',
                          height: '24px',
                          borderRadius: '12px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    )}
                  </Box>

                  {/* Loading State or No Data Message */}
                  {paymentReceiptDetails.length === 0 && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      textAlign: 'center'
                    }}>
                      <CircularProgress
                        size={32}
                        sx={{
                          color: currentTheme.primary,
                          mb: 2
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontSize: '13px',
                          fontWeight: 500,
                          mb: 1
                        }}
                      >
                        Loading {formData.IsType ? 'purchase' : 'sale'} data...
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '12px'
                        }}
                      >
                        Please wait while we fetch the latest transactions
                      </Typography>
                    </Box>
                  )}

                  {/* No Data Found Message */}
                  {paymentReceiptDetails.length === 1 && paymentReceiptDetails[0]?.noData && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      textAlign: 'center'
                    }}>
                      <Warning
                        sx={{
                          color: '#fbbf24',
                          fontSize: 40,
                          mb: 2
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontSize: '13px',
                          fontWeight: 500,
                          mb: 1
                        }}
                      >
                        No {formData.IsType ? 'purchase bills' : 'sale bills'} found
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '12px'
                        }}
                      >
                        This master has no pending {formData.IsType ? 'purchase' : 'sale'} transactions
                      </Typography>
                    </Box>
                  )}

                  {/* Data Table */}
                  {paymentReceiptDetails.length > 0 && !paymentReceiptDetails[0]?.noData && !paymentReceiptDetails[0]?.isDirectPayment && (
                    <Box sx={{
                      maxHeight: 300,
                      overflow: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#fff'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'center',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '8%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Select
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'left',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '25%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Bill Details
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'right',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '15%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Bill Amount
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'right',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '15%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Paid Amount
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'right',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '15%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Pending Amount
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'right',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                              width: '15%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              {formData.IsType ? 'Payment Amount' : 'Receipt Amount'}
                            </th>
                            <th style={{
                              padding: '8px 6px',
                              textAlign: 'center',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '12px',
                              width: '12%',
                              borderBottom: '1px solid #ddd'
                            }}>
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentReceiptDetails.map((detail, index) => (
                            <tr
                              key={detail.Id}
                              style={{
                                backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
                                borderBottom: '1px solid #eee'
                              }}
                            >
                              <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                                <Checkbox
                                  checked={detail.IsSelected || false}
                                  onChange={(e) => handleDetailChange(index, 'IsSelected', e.target.checked)}
                                  disabled={isViewMode}
                                  size="small"
                                  sx={{
                                    color: '#666',
                                    '&.Mui-checked': {
                                      color: '#1976d2',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '18px'
                                    }
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px 6px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px', color: '#333', lineHeight: 1.3 }}>
                                  {detail.Description}
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>
                                  {formatCurrency(detail.Amount)}
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#10b981' }}>
                                  {formatCurrency(detail.PaymentAmount || 0)}
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500, color: '#dc6363' }}>
                                  {formatCurrency(detail.PendingAmount)}
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                <StyledTextField
                                  size="small"
                                  type="number"
                                  value={detail.PaymentAmount || ''}
                                  onChange={(e) => handleDetailChange(index, 'PaymentAmount', e.target.value)}
                                  disabled={isViewMode || !detail.IsSelected}
                                  inputProps={{
                                    min: 0,
                                    max: detail.PendingAmount,
                                    step: 0.01,
                                    style: { textAlign: 'right', fontSize: '12px', padding: '4px', fontWeight: 500 }
                                  }}
                                  transactiontype={transactionType}
                                  sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                      height: '28px',
                                      borderRadius: '4px'
                                    }
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                                <Chip
                                  label={detail.PendingAmount > 0 ? 'Pending' : 'Paid'}
                                  color={detail.PendingAmount > 0 ? 'warning' : 'success'}
                                  size="small"
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    height: '20px',
                                    borderRadius: '10px',
                                    ...(detail.PendingAmount > 0 ? {
                                      backgroundColor: '#fff3cd',
                                      color: '#856404'
                                    } : {
                                      backgroundColor: '#d4edda',
                                      color: '#155724'
                                    })
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* No Master Selected Message */}
            {!formData.MasterId && (
              <Box sx={{
                border: '1px dashed #ccc',
                borderRadius: '8px',
                p: 3,
                textAlign: 'center',
                backgroundColor: '#f9f9f9'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 1
                  }}
                >
                  Select a master to view related transactions
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999',
                    fontSize: '12px'
                  }}
                >
                  Choose a master to see {formData.IsType ? 'purchase bills' : 'sale bills'}
                </Typography>
              </Box>
            )}

          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 1.5,
          py: 1,
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          gap: 1,
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(modalAction === 'view' || modalAction === 'edit') && formData.Id && onPrint && (
              <StyledButton
                onClick={() => onPrint(formData.Id)}
                variant="contained"
                size="small"
                startIcon={<Print />}
                sx={{
                  background: transactionType === 'payment' ? '#dc6363' : '#429178',
                  '&:hover': {
                    background: transactionType === 'payment' ? '#b91c1c' : '#047857'
                  }
                }}
              >
                Download PDF
              </StyledButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <StyledButton
              onClick={handleCancelClick}
              variant="outlined"
              size="small"
              transactiontype={transactionType}
            >
              Cancel
            </StyledButton>
            {!isViewMode && (
              <StyledButton
                onClick={handleSaveWithValidation}
                variant="contained"
                size="small"
                disabled={loading}
                transactiontype={transactionType}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={14} sx={{ color: 'white' }} />
                    <span>Saving...</span>
                  </Box>
                ) : (
                  <>Save</>
                )}
              </StyledButton>
            )}
          </Box>
        </DialogActions>

      </StyledDialog>

      {/* Cancel Confirmation Dialog */}
      <StyledDialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        transactiontype={transactionType}
        PaperProps={{ sx: { borderRadius: '16px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ color: '#64748b', fontSize: '0.95rem', mb: 1 }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc6363', fontSize: '0.9rem', fontWeight: 600 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <StyledButton
            onClick={handleStayOnPage}
            variant="outlined"
            transactiontype={transactionType}
            sx={{ minWidth: '120px' }}
          >
            Stay on Page
          </StyledButton>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{
              backgroundColor: '#dc6363',
              '&:hover': { backgroundColor: '#b91c1c' },
              minWidth: '120px',
              fontWeight: 600
            }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              fontSize: '1.2rem'
            },
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentReceiptsModal;